<script setup>
let { data: contact, pending } = await useWordpressData('wp-remote/contact')

let normalizedPhone = computed(() =>
  pending.value ? '' : contact.value.phone.replace(/^\+/, '00').replace(/[^0-9]/g, ''),
)
let addressLines = computed(() => (pending.value ? [] : contact.value.address.split('\n')))
</script>

<template>
  <footer v-if="!pending && contact">
    <div class="container-wide">
      <div class="get-in-touch">
        <span class="get-in-touch-project">Like this stuff?</span>
        <ArrowLink :href="contact.contactPagePath">Get in touch</ArrowLink>
      </div>

      <div class="footer-layout">
        <div>
          <div class="footer-row">
            <!-- prettier-ignore -->
            <svg class="footer-logo" aria-hidden="true" focusable="false" width="478" height="117" viewBox="0 0 478 117" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><title>module+</title><defs><polygon id="a" points="-3.31965087e-15 8.32667268e-17 421.7589 8.32667268e-17 421.7589 91.686 -3.31965087e-15 91.686"/><polygon id="c" points="-3.31965087e-15 8.32667268e-17 421.7589 8.32667268e-17 421.7589 91.686 -3.31965087e-15 91.686"/></defs><g fill="none" fill-rule="evenodd"><g fill="#FFCE32" fill-rule="nonzero"><path d="M444.637 19.365h18.836V.53h-18.836v18.836Zm4.283-14.547h10.264V15.08H448.92V4.818Z"/><path d="M459.47 23.114v14.548h-10.476V23.114h-24.28V41.95h19.923v19.993h18.836V41.95h7.1l1.306-4.288h-8.12V27.4h11.245l1.305-4.286h-16.84ZM429 27.4h15.706v10.262H429V27.4Zm19.92 14.55h10.265v15.71H448.92V41.95Z"/></g><g transform="translate(.69 23.66)"><mask id="b" fill="#fff"><use xlink:href="#a"/></mask><path d="M421.757 55.442c-.737-20.526-12.725-30.662-31.119-30.662-20.04 0-32.817 13.807-32.817 32.982 0 20.199 12.651 33.622 33.945 33.622 9.394 0 20.04-3.324 26.554-10.227l-9.77-9.844c-3.507 3.707-10.897 6.337-16.534 6.337-10.772 0-17.732-6.324-18.734-14.123h47.683s.841-3.935.792-8.085Zm-47.903-4.583c2.255-8.182 9.365-12.272 17.632-12.272 8.768 0 14.684 4.09 15.686 12.272h-33.318Z" fill="#FFF" fill-rule="nonzero" mask="url(#b)"/></g><g transform="translate(.69 23.66)"><mask id="d" fill="#fff"><use xlink:href="#c"/></mask><path d="M58.108 90.266v-34.09c0-8.393 5.294-15.754 13.817-15.754 8.522 0 12.009 7.102 12.009 15.496v34.348h15.754V55.918c0-19.112-8.91-30.217-26.988-30.217-8.265 0-15.625 2.583-21.307 11.622-3.615-8.781-11.234-11.88-18.853-11.88-6.069 0-13.429 2.324-16.786 8.781l-1.163-7.748H0v63.79h15.754v-34.09c0-8.393 5.036-16.012 13.558-16.012 8.652 0 13.042 7.619 13.042 16.012v34.09h15.754Zm81.466-64.952c-20.273 0-32.928 14.979-32.928 33.186 0 18.337 12.397 33.186 33.057 33.186 20.661 0 33.186-14.849 33.186-33.186 0-18.207-13.042-33.186-33.315-33.186Zm.129 14.333c10.589 0 17.303 9.298 17.303 18.853 0 9.685-5.81 18.724-17.303 18.724-11.492 0-17.303-9.039-17.303-18.724 0-9.555 5.94-18.853 17.303-18.853Zm73.533.129c9.943 0 18.466 7.49 18.466 18.595 0 11.493-8.523 18.724-18.466 18.724-10.072 0-18.078-7.619-18.078-18.724 0-11.492 8.006-18.595 18.078-18.595ZM232.735.005v35.252c-3.745-6.586-14.205-10.201-20.919-10.201-18.595 0-32.412 11.363-32.412 33.315 0 20.919 14.075 33.315 32.799 33.315 7.748 0 15.625-2.582 20.532-10.201l1.033 8.781h14.72V.005h-15.753Zm26.38 26.6V60.05c0 19.757 11.105 31.249 26.343 31.249 8.91 0 14.979-3.099 21.564-9.556l1.033 8.652h14.075v-63.79h-15.624v32.541c0 9.685-6.586 17.82-16.271 17.82-10.072 0-15.366-7.361-15.366-17.045V26.605h-15.754Zm75.17 63.656h15.625V0h-15.625v90.261Z" fill="#FFF" fill-rule="nonzero" mask="url(#d)"/></g></g></svg>
            <span class="sr-only">module+</span> GmbH

            <template v-for="(addressLine, index) in addressLines">
              <Slash class="footer-slash" />
              {{ addressLine }}
            </template>
          </div>
          <div class="footer-row">
            <a :href="`tel:${normalizedPhone}`">
              <!-- prettier-ignore -->
              <svg class="footer-phone-icon" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M6.482 3.671C6.274 1.274 3.774.098 3.67.051a.523.523 0 0 0-.317-.043C.467.488.032 2.168.015 2.236a.555.555 0 0 0 .01.296C3.469 13.214 10.62 15.194 12.97 15.846c.18.05.33.09.443.127a.542.542 0 0 0 .394-.023c.072-.032 1.77-.833 2.186-3.445a.551.551 0 0 0-.052-.334c-.037-.072-.926-1.764-3.39-2.362a.533.533 0 0 0-.482.115c-.778.665-1.851 1.372-2.315 1.445-3.106-1.52-4.84-4.436-4.906-4.989-.038-.31.674-1.403 1.492-2.291a.546.546 0 0 0 .142-.418" fill="#FFF" fill-rule="evenodd"/></svg>
              <span class="sr-only">Telefon: </span>

              {{ contact.phone }}
            </a>
            <Slash class="footer-slash" />
            <a :href="`mailto:${contact.email}`">
              <!-- prettier-ignore -->
              <svg class="footer-email-icon" aria-hidden="true" focusable="false" width="16" height="12" viewBox="0 0 16 12" xmlns="http://www.w3.org/2000/svg"><path d="M15 0H1a1 1 0 0 0-1 1v1.68l8 3.199 8-3.2V1A1 1 0 0 0 15 0ZM0 4.312V11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V4.312l-8 3.2-8-3.2Z" fill="#FFF" fill-rule="evenodd"/></svg>
              <span class="sr-only">E-Mail: </span>

              {{ contact.email }}
            </a>
          </div>
        </div>
        <div class="footer-socials">
          Follow us:

          <div class="socials-links">
            <a v-if="contact.linkedin" :href="contact.linkedin" target="_blank" rel="noopener">
              <!-- prettier-ignore -->
              <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M14.857 0H1.138C.511 0 0 .518 0 1.154v13.692C0 15.482.51 16 1.138 16h13.72A1.15 1.15 0 0 0 16 14.846V1.154A1.15 1.15 0 0 0 14.857 0ZM4.836 13.714H2.464V6.079H4.84v7.635h-.003ZM3.649 5.036a1.376 1.376 0 0 1 0-2.75 1.374 1.374 0 1 1 0 2.75Zm10.076 8.678h-2.372V10c0-.885-.017-2.025-1.232-2.025-1.235 0-1.425.965-1.425 1.96v3.779h-2.37V6.079H8.6v1.043h.032c.318-.6 1.094-1.233 2.246-1.233 2.4 0 2.847 1.582 2.847 3.64v4.185Z" fill="#FFF" fill-rule="evenodd"/></svg>
              <span class="sr-only">LinkedIn</span>
            </a>

            <a v-if="contact.instagram" :href="contact.instagram" target="_blank" rel="noopener">
              <!-- prettier-ignore -->
              <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M11.366.018c2.662.031 4.581 1.966 4.624 4.636L16 5.75l-.006 2.195h.003c0 1.119.008 2.238-.002 3.357-.026 2.724-1.998 4.69-4.708 4.693-2.193.005-4.387.006-6.58 0C2.026 15.99.04 14.038.015 11.357-.006 9.118-.005 6.879.02 4.642.05 1.98 1.987.047 4.653.018c2.237-.024 4.475-.024 6.713 0Zm-6.722 1.49c-1.866.015-3.149 1.293-3.165 3.149-.019 2.26-.021 4.52-.005 6.779.013 1.824 1.287 3.097 3.112 3.128l1.14.01 2.28-.006v.002l2.238.005c.373 0 .746-.003 1.118-.008 1.9-.025 3.164-1.296 3.175-3.201.011-2.24.01-4.477-.007-6.715-.012-1.808-1.307-3.123-3.108-3.14-2.26-.022-4.52-.02-6.778-.003Zm3.381 2.383c2.273 0 4.127 1.87 4.122 4.157-.006 2.327-1.858 4.152-4.199 4.132a4.114 4.114 0 0 1-4.086-4.177 4.137 4.137 0 0 1 4.163-4.112ZM8.03 5.34c-1.484-.016-2.726 1.22-2.723 2.71A2.687 2.687 0 0 0 7.98 10.73a2.681 2.681 0 0 0 2.72-2.694A2.718 2.718 0 0 0 8.03 5.34Zm4.28-2.56c.54.007.936.42.928.965-.009.547-.41.937-.958.93a.92.92 0 0 1-.932-.959.933.933 0 0 1 .962-.936Z" fill="#FFF" fill-rule="evenodd"/></svg>
              <span class="sr-only">Instagram</span>
            </a>

            <a v-if="contact.facebook" :href="contact.facebook" target="_blank" rel="noopener">
              <!-- prettier-ignore -->
              <svg aria-hidden="true" focusable="false" width="8" height="16" viewBox="0 0 8 16" xmlns="http://www.w3.org/2000/svg"><path d="M2.366 8.16H0V5.525h2.362V3.452C2.369 1.399 3.286.311 5.322.045 6.006-.044 6.713.026 7.41.024c.374-.001.512.177.496.552-.025.603-.007 1.209-.007 1.904H6.778c-1.352.005-1.651.298-1.652 1.619v1.4h2.668c-.103.83-.174 1.576-.307 2.312-.026.139-.298.322-.466.333-.605.04-1.215.015-1.885.015V15h-2.77V8.16Z" fill="#FFF" fill-rule="evenodd"/></svg>
              <span class="sr-only">facebook</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
footer {
  --padding: 4rem;

  background-color: #000000;
  color: #fff;
  padding: var(--padding) 0;
  font-size: 14px;

  @include mediaSM {
    --padding: 5rem;
    font-size: 16px;
  }

  @include mediaMD {
    --padding: 6rem;
    font-size: 18px;
  }

  @include mediaLG {
    --padding: 7rem;
    font-size: 20px;
  }

  @include mediaXL {
    font-size: 22px;
  }

  @at-root :root[data-no-footer] & {
    display: none;
  }

  .h1,
  .h2,
  .h3 {
    color: #fff;
  }

  .h2 {
    font-size: 19px;
  }

  .footer-layout {
    @include mediaMD {
      display: flex;
      justify-content: space-between;
    }
  }

  .footer-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;

    & + .footer-row {
      margin-top: 0.4em;
    }
  }

  .footer-phone-icon {
    margin-bottom: -2px;
    margin-right: 0.25em;
  }

  .footer-email-icon {
    margin-right: 0.25em;
  }

  a {
    color: inherit;
    text-decoration: none;
  }
}

.footer-logo {
  height: 1.15em;
  width: auto;
  margin-top: -0.6em;
  margin-right: 0.25em;
}

.footer-socials {
  font-weight: 600;
  display: flex;

  .socials-links {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    position: relative;
    top: 0.3em;
    margin-left: 12px;
    line-height: 1;

    svg {
      display: block;
    }
  }
}

.footer-slash {
  --slash-size: 1em;
  --slash-color: var(--primary-color);
  margin: -0.05em 0.75em 0;
}

.get-in-touch {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  font-size: 24px;
  --arrow-link-icon-size: 0.5em;
  --arrow-link-icon-offset: 0.1em;

  @include mediaSM {
    font-size: 28px;
  }

  @include mediaMD {
    font-size: 32px;
  }

  @include mediaLG {
    font-size: 36px;
  }

  .get-in-touch-project {
    display: none;

    @at-root :root.project & {
      display: block;
      margin-right: 0.5em;
    }
  }
}
</style>
